import { parseRoute, screenIdentity } from '@visious/core';

/**
 * The router's scroll behaviour (2026-08-02): a screen switch reads from the top, and
 * Back/Forward put the reader back where they were on the entry they return to.
 *
 * `history.scrollRestoration` goes to `'manual'` because the browser's own restore is built for
 * documents, not for an SPA: it fires the moment the URL changes, while the screen it remembers is
 * still one render and several responses away — so it either lands on the previous screen's pixels
 * or gives up against a page that is momentarily 400 px tall. We keep the browser's *bookkeeping*
 * though: every entry gets an id in `history.state`, positions are filed under those ids in
 * `sessionStorage` (per-tab, like the back stack itself; survives a reload, dies with the tab),
 * and a restore polls until the content is tall enough to hold the position or a deadline passes.
 *
 * What counts as "a screen switch" is `screenIdentity` in core — one identity is the same mounted
 * page wearing different controls (a search filter, a channel sort), and navigating within it must
 * not move the reader. The web half here is only storage and timing.
 */

const STORE_KEY = 'visious.scroll';

/** Entries beyond this lose their position, oldest first — a back stack this deep is theoretical. */
const MAX_POSITIONS = 100;

/** How long a restore keeps waiting for async content to make the page tall enough. */
const RESTORE_WINDOW_MS = 4000;

interface ScrollStore {
  /** The id mint. Persisted so a reload cannot re-issue ids the map already files positions under. */
  seq: number;
  /** Entry id → `scrollY`. Insertion-ordered, which is what {@link trim} evicts by. */
  positions: Record<string, number>;
}

let store: ScrollStore | null = null;

/** The id of the history entry the reader is on — the key their position will be saved under. */
let currentId: string | null = null;

/** Bumped to abandon a pending restore: a navigation or a fresh restore makes the old target moot. */
let generation = 0;

function loadStore(): ScrollStore {
  if (store !== null) return store;
  try {
    const raw = globalThis.sessionStorage.getItem(STORE_KEY);
    if (raw !== null) {
      const parsed: unknown = JSON.parse(raw);
      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        typeof (parsed as { seq?: unknown }).seq === 'number'
      ) {
        const positions = (parsed as { positions?: unknown }).positions;
        store = {
          seq: (parsed as { seq: number }).seq,
          positions:
            typeof positions === 'object' && positions !== null
              ? (positions as Record<string, number>)
              : {},
        };
        return store;
      }
    }
  } catch {
    // Unreadable storage means starting fresh, not failing to scroll at all.
  }
  store = { seq: 0, positions: {} };
  return store;
}

function persist(): void {
  if (store === null) return;
  try {
    globalThis.sessionStorage.setItem(STORE_KEY, JSON.stringify(store));
  } catch {
    // Best-effort: with storage refused, positions still work for this document's lifetime.
  }
}

function trim(positions: Record<string, number>): void {
  const keys = Object.keys(positions);
  for (let extra = keys.length - MAX_POSITIONS; extra > 0; extra -= 1) {
    const oldest = keys.shift();
    if (oldest === undefined) return;
    delete positions[oldest];
  }
}

function mintId(): string {
  const opened = loadStore();
  opened.seq += 1;
  // The 's' prefix keeps the key non-numeric, so the object's insertion order — what trim() evicts
  // by — is the spec-guaranteed string-key order rather than the ascending-integer reordering.
  return `s${opened.seq}`;
}

function saveCurrent(): void {
  if (currentId === null) return;
  const opened = loadStore();
  opened.positions[currentId] = Math.round(globalThis.scrollY);
  trim(opened.positions);
  persist();
}

function idOf(state: unknown): string | null {
  if (typeof state !== 'object' || state === null) return null;
  const id = (state as { visiousScroll?: unknown }).visiousScroll;
  return typeof id === 'string' ? id : null;
}

/**
 * Scroll to `y` once the page can hold it. Async content means the position usually exceeds the
 * document's height at popstate time, so this clamps immediately (the reader must not be left
 * mid-way down the *previous* screen) and keeps nudging toward the target as the page grows —
 * until it fits, the deadline passes, or the reader scrolls somewhere themselves, whose choice wins.
 */
function restore(y: number): void {
  const gen = ++generation;
  const target = Math.max(0, Math.round(y));
  const deadline = performance.now() + RESTORE_WINDOW_MS;
  let lastWritten: number | null = null;

  const tick = (): void => {
    if (gen !== generation) return;
    if (lastWritten !== null && Math.abs(globalThis.scrollY - lastWritten) > 1) return;
    const limit = Math.max(0, document.documentElement.scrollHeight - globalThis.innerHeight);
    const reachable = Math.min(target, limit);
    if (Math.round(globalThis.scrollY) !== reachable) globalThis.scrollTo(0, reachable);
    lastWritten = globalThis.scrollY;
    if (reachable >= target || performance.now() > deadline) return;
    globalThis.requestAnimationFrame(tick);
  };
  tick();
}

/**
 * The `history.state` for a push: files the position of the entry being left, and mints the id the
 * new entry's own position will live under. Called by `navigate` in the same breath as `pushState`.
 */
export function scrollStateForPush(): { visiousScroll: string } {
  saveCurrent();
  const id = mintId();
  currentId = id;
  persist();
  return { visiousScroll: id };
}

/**
 * The `history.state` for a replace: whatever the entry already carries. A canonicalising rewrite
 * changes the URL's spelling, not which entry the reader is on, so the id — and with it the saved
 * position — must survive.
 */
export function preservedScrollState(): unknown {
  return globalThis.history.state;
}

/** After a push landed: pending restores are moot, and a genuine screen switch reads from the top. */
export function settleAfterPush(fromHref: string, toHref: string): void {
  generation += 1;
  if (screenIdentity(parseRoute(fromHref)) !== screenIdentity(parseRoute(toHref))) {
    globalThis.scrollTo(0, 0);
  }
}

/**
 * Wire the whole behaviour up. Called once from `main.tsx`, before React renders — the boot itself
 * may be a reload (F5 keeps the entry, so it keeps the position) and the restore should be racing
 * the first render, not waiting for it.
 */
export function initScrollBehavior(): void {
  globalThis.history.scrollRestoration = 'manual';

  // Adopt the entry's id, or stamp one if this entry predates us (a fresh tab, a deploy mid-session).
  const adopted = idOf(globalThis.history.state);
  currentId = adopted ?? mintId();
  if (adopted === null) {
    globalThis.history.replaceState({ visiousScroll: currentId }, '');
    persist();
  } else {
    const saved = loadStore().positions[currentId];
    if (saved !== undefined) restore(saved);
  }

  globalThis.addEventListener('popstate', (event) => {
    // The document hasn't changed yet, so `scrollY` still measures the entry being left.
    saveCurrent();
    const id = idOf(event.state);
    if (id === null) {
      // An entry from before this module existed. Stamp it now; there is no position to return to.
      currentId = mintId();
      globalThis.history.replaceState({ visiousScroll: currentId }, '');
      persist();
      restore(0);
      return;
    }
    currentId = id;
    restore(loadStore().positions[id] ?? 0);
  });

  // A reload and the jump-out to the consent page both leave without a popstate or a push — this is
  // the last moment the position can be filed so the way back finds it.
  globalThis.addEventListener('pagehide', saveCurrent);
}
